import { useState, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import SEOHead from '@/components/feature/SEOHead';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import BackToTop from '@/components/feature/BackToTop';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ArticleCard from '@/components/base/ArticleCard';
import CategoryTab from '@/components/base/CategoryTab';
import { articles } from '@/mocks/articles';
import { categories } from '@/mocks/categories';
import { siteInfo } from '@/mocks/site-info';

export default function Categories() {
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = searchParams.get('tab') || 'all';
  const [searchText, setSearchText] = useState('');

  const filteredArticles = useMemo(() => {
    let filtered = activeTab === 'all' ? articles : articles.filter((a) => a.category === activeTab);
    if (searchText.trim()) {
      const query = searchText.trim().toLowerCase();
      filtered = filtered.filter(
        (a) =>
          a.title.toLowerCase().includes(query) ||
          a.excerpt.toLowerCase().includes(query) ||
          a.tags.some((t) => t.toLowerCase().includes(query))
      );
    }
    return filtered;
  }, [activeTab, searchText]);

  const handleTabChange = (slug: string) => {
    setSearchParams(slug === 'all' ? {} : { tab: slug });
  };

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [activeTab]);

  const activeCategoryName = activeTab === 'all' ? '全部文章' : categories.find((c) => c.slug === activeTab)?.name || '';

  const pageTitle = activeTab === 'all'
    ? '分类导航 - 全部文章'
    : `${activeCategoryName} - 51cg网`;

  return (
    <>
      <SEOHead
        title={activeTab === 'all' ? '分类导航 - 全部文章' : activeCategoryName}
        description={`浏览51cg网${activeCategoryName}分类下的所有文章和教程，包括平台入口、注册登录、使用教程、功能解析等内容，助您全面掌握51cg网的使用技巧。`}
        keywords={`51cg网${activeCategoryName ? `,${activeCategoryName}` : ''},51cg网使用教程,51cg网功能,51cg网分类`}
        canonicalUrl={`https://www.51cg.com/categories${activeTab !== 'all' ? `?tab=${activeTab}` : ''}`}
      />

      <Navbar />

      <main className="pt-16">
        <div className="bg-background-100">
          <div className="max-w-7xl mx-auto px-4 md:px-6 py-10">
            <Breadcrumb
              items={[{ label: '首页', to: '/' }, { label: '分类导航' }]}
              categorySlug={activeTab !== 'all' ? activeTab : undefined}
            />
            <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mt-4 mb-2">
              {activeTab === 'all' ? '全部分类' : activeCategoryName}
            </h1>
            <p className="text-foreground-500 text-sm">浏览所有文章和教程，找到您需要的内容</p>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 md:px-6 py-8">
          <div className="flex flex-col lg:flex-row gap-8">
            <div className="lg:w-3/4">
              <div className="mb-6">
                <div className="relative">
                  <i className="ri-search-line absolute left-3.5 top-1/2 -translate-y-1/2 text-foreground-400 text-sm"></i>
                  <input
                    type="text"
                    placeholder="搜索文章..."
                    value={searchText}
                    onChange={(e) => setSearchText(e.target.value)}
                    className="w-full pl-10 pr-4 py-2.5 text-sm rounded-md border border-background-300 bg-background-50 text-foreground-900 placeholder-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-300 transition-colors"
                  />
                </div>
              </div>

              <div className="mb-8 overflow-x-auto pb-2">
                <CategoryTab
                  categories={categories}
                  activeSlug={activeTab}
                  onSelect={handleTabChange}
                />
              </div>

              {filteredArticles.length > 0 ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  {filteredArticles.map((article) => (
                    <ArticleCard key={article.slug} article={article} />
                  ))}
                </div>
              ) : (
                <div className="text-center py-20">
                  <div className="w-16 h-16 mx-auto rounded-full bg-background-100 flex items-center justify-center mb-4">
                    <i className="ri-file-search-line text-foreground-400 text-2xl"></i>
                  </div>
                  <p className="text-foreground-500 text-sm">没有找到相关文章，试试其他关键词吧</p>
                </div>
              )}
            </div>

            <aside className="lg:w-1/4">
              <div className="bg-background-100 rounded-lg p-5 border border-background-200/70 sticky top-24">
                <h3 className="font-heading text-base font-semibold text-foreground-900 mb-4">热门标签</h3>
                <div className="flex flex-wrap gap-2">
                  {['51cg网', '官方入口', '注册登录', '使用教程', '功能介绍', '安全防护', '最新地址', '常见问题'].map((tag) => (
                    <span
                      key={tag}
                      className="bg-background-50 text-foreground-600 text-xs px-3 py-1.5 rounded-full border border-background-200/70 cursor-pointer hover:border-primary-300 hover:text-primary-500 transition-colors whitespace-nowrap"
                      onClick={() => setSearchText(tag)}
                    >
                      #{tag}
                    </span>
                  ))}
                </div>

                <div className="mt-6 pt-6 border-t border-background-200/70">
                  <h3 className="font-heading text-base font-semibold text-foreground-900 mb-3">关于{siteInfo.name}</h3>
                  <p className="text-foreground-500 text-sm leading-relaxed">{siteInfo.description}</p>
                </div>
              </div>
            </aside>
          </div>
        </div>
      </main>

      <Footer />
      <BackToTop />
    </>
  );
}